import { portalText } from "./i18n/portal.js";
import type { PortalMessage } from "./i18n/message.js";
/**
 * The route tree.
 *
 * Code-based rather than file-based, so there is no generated `routeTree.gen.ts` to keep in
 * the repository and no build step between a route and its type. The whole tree is one file
 * anyone can read top to bottom.
 *
 * TYPED SEARCH PARAMETERS ARE THE POINT. `validateSearch` is where the console's shareable
 * state is defined: the wizard's step, and the agent surface's tab, range, outcome filter,
 * search text and selected conversation. Every one of them is parsed into a union or a
 * bounded number, so a hand-edited URL produces a valid screen rather than a blank one — and
 * `Link` call sites are type-checked against the same shapes, which is why a tab that does
 * not exist is a compile error rather than an empty tab body.
 *
 * The validators are total functions over `unknown`, and they return a well-formed value for
 * garbage input. That is not a configuration fallback — the URL is untrusted input from a
 * person's address bar, not configuration, and "the tab you asked for does not exist" has a
 * correct answer.
 */
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  stripSearchParams,
  useLocation,
  type AnyRoute,
  type RouterHistory,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { parseConsoleSearch, stringifyConsoleSearch } from "./search-serialisation.js";
import { VoiceAgentScreen, type VoiceAgentScreenSearch } from "./routes/agents/[id]/agent-screen.js";
import { AuthScreen, entrySearchOf, type EntrySearch } from "./routes/auth/auth-screen.js";
import { JoinScreen } from "./routes/auth/join-screen.js";
// plan-019 REG: the help assistant's root component and fallback page (WAPP-HOST fills both).
import { HelpRoot } from "./help/help-root.js";
import { HelpFallbackScreen } from "./routes/help/help-fallback-screen.js";
import { BuildingScreen } from "./routes/building/building-screen.js";
import { DashboardScreen } from "./routes/dashboard/dashboard-screen.js";
import { FirstRunScreen } from "./routes/first-run/first-run-screen.js";
import { AgentTemplatePickerScreen } from "./routes/agents/new/template-picker-screen.js";
import {
  VoiceAgentListScreen,
  isVoiceAgentFilter,
  type VoiceAgentFilter,
} from "./routes/agents/voice-agent-list-screen.js";
import { WizardScreen } from "./routes/agents/new/wizard-screen.js";
import { wizardShareOf, wizardStepOf } from "./routes/agents/new/wizard-rules.js";
import { MediumScreen } from "./routes/messaging/medium-screen.js";
import { MessagingScreen } from "./routes/messaging/messaging-screen.js";
import { NumbersScreen } from "./routes/numbers/numbers-screen.js";
import { BillingScreen } from "./routes/billing/billing-screen.js";
import { billingSearchOf, type BillingSearch } from "./routes/billing/plan-billing.js";
import { LedgerScreen } from "./routes/billing/ledger-screen.js";
import { PayoutsScreen } from "./routes/payouts/payouts-screen.js";
import { LiveActivityScreen } from "./routes/live-activity/live-activity-screen.js";
import { SettingsScreen } from "./routes/settings/settings-screen.js";
import { SandboxConsoleScreen } from "./routes/test-console/test-console-screen.js";
import { WorkspaceDetailScreen } from "./routes/workspaces/workspace-detail-screen.js";
import { WorkspacesScreen, workspacesSearchOf, type WorkspacesSearch } from "./routes/workspaces/workspaces-screen.js";
import { Shell } from "./shell/shell.js";
import { voiceAgentTabOfAddress, type VoiceAgentTabId } from "./routes/agents/[id]/agent-tabs.js";
import { isConcernPane, type ConcernPane } from "./routes/agents/[id]/review-tab.js";
import {
  isCustomerOrderFilter,
  type CustomerOrderFilter,
} from "./routes/agents/[id]/orders/order-book-gateway.js";
import { CustomerOrderScreen } from "./routes/orders/order-screen.js";
// P14. Seven console surfaces; the eighth, Opt out, is an unauthenticated page on the public
// site (`apps/web-public`) and has no route here — see the note above its absence, below.
import { ContactListScreen } from "./routes/contact-lists/contact-list-screen.js";
import { ContactListDetailScreen } from "./routes/contact-lists/contact-list-detail-screen.js";
import { NewCampaignScreen } from "./routes/campaigns/new-campaign-screen.js";
import { CampaignScreen } from "./routes/campaigns/campaign-screen.js";
import {
  SurveyResultScreen,
  type SurveyResultViewMode,
} from "./routes/campaigns/survey-result-screen.js";
import { isSurveyResultRange, type RespondentFilterValue } from "./routes/campaigns/survey-result-copy.js";
import { DoNotCallScreen } from "./routes/do-not-call/do-not-call-screen.js";
import { ComplianceScreen } from "./routes/compliance/compliance-screen.js";
import { AuditEntryScreen } from "./routes/audit/audit-entry-screen.js";
import { impersonationHandoffRouteUnder } from "./routes/impersonation/impersonation-route.js";
// plan-014 F3d (the hand-off from F3a): a person confirms an operator action from the emailed link.
import { confirmOperatorActionRouteUnder } from "./routes/confirm-operator-action/confirm-operator-action-route.js";
// plan-034 WAPP-APPSIGNIN: the wetalk app's consent page and its static return page, and the hold that brings a person
// back to the consent page after the portal's own sign-in.
import { appSignInFinishRouteUnder, appSignInRouteUnder } from "./routes/app-sign-in/app-sign-in-route.js";
import { appSignInReturnOf, tabStorage } from "./routes/app-sign-in/app-sign-in-request.js";
// plan-014 GAP (the MAIL hand-off): a person's own emailed links — confirm the address, confirm an email change.
import { emailChangeRouteUnder, verifyEmailRouteUnder } from "./routes/mail-landing/mail-landing-route.js";
// 2026-10-12 (owner request): "Forgot your password?" — the two way-in pages of the password reset.
import { forgotPasswordRouteUnder, resetPasswordRouteUnder } from "./routes/password-reset/password-reset-route.js";
import { DocsScreen } from "./routes/docs/docs-screen.js";
import { isDocsArticleId } from "./routes/docs/docs-content.js";
// plan-014 REG (ED-15; sole owner of the plan-014 lines in this file): the thirteen customer pages of
// plan-014 §4.1, each a stub module its owning unit replaces (`PLAN_014_UNCONNECTED`).
import { AccountOverviewScreen, accountOverviewSearchOf, type AccountOverviewSearch } from "./routes/account-admin/overview/account-overview-screen.js";
import { AccountTeamScreen, accountTeamSearchOf, type AccountTeamSearch } from "./routes/account-admin/people/account-team-screen.js";
import { AccountSecurityScreen, accountSecuritySearchOf, type AccountSecuritySearch } from "./routes/account-admin/security/account-security-screen.js";
import { AccountSharingScreen, accountSharingSearchOf, type AccountSharingSearch } from "./routes/account-admin/sharing/account-sharing-screen.js";
import { AccountLifecycleScreen, accountLifecycleSearchOf, type AccountLifecycleSearch } from "./routes/account-admin/archive-close/account-lifecycle-screen.js";
import { FundingScreen, fundingSearchOf, type FundingSearch } from "./routes/finance/funding/funding-screen.js";
import { PlansScreen, plansSearchOf, type PlansSearch } from "./routes/finance/plans/plans-screen.js";
import { AllowanceScreen, allowanceSearchOf, type AllowanceSearch } from "./routes/finance/allowances/allowance-screen.js";
import { FinancialDocumentScreen, financialDocumentSearchOf, type FinancialDocumentSearch } from "./routes/finance/documents/financial-document-screen.js";
import { PostpaidScreen, postpaidSearchOf, type PostpaidSearch } from "./routes/finance/postpaid/postpaid-screen.js";
import { PersonalSecurityScreen, personalSecuritySearchOf, type PersonalSecuritySearch } from "./routes/personal-security/personal-security-screen.js";
import { AccountCreationFlow, accountCreationSearchOf, type AccountCreationSearch } from "./routes/start/create/account-creation-flow.js";
import { InvitationAcceptScreen, invitationAcceptSearchOf, type InvitationAcceptSearch } from "./routes/start/accept/invitation-accept-screen.js";
import { authContextQuery, authSessionQuery, workspaceQuery } from "./api/query.js";
import { ShellFailure } from "./shell/shell-failure.js";
import { PageFailure } from "./shell/page-failure.js";
import type {
  AnalyticsRange,
  AuditEventClassView,
  ConsoleGateway,
  DoNotCallFilterView,
  HistoryChannelLabelFilter,
  HistoryOutcomeFilter,
  HistoryRange,
  SurveyResultRangeView,
  SurveyVariantView,
} from "./api/gateway.js";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    readonly pageTitle?: PortalMessage;
    /**
     * The top bar names the VoiceAgent in `$voiceAgentId` (the prototype's "Roma orders"),
     * falling back to `pageTitle` only until the agent list has arrived.
     */
    readonly pageTitleIsVoiceAgentName?: boolean;
  }
}

export interface RouterContext {
  readonly gateway: ConsoleGateway;
  readonly queryClient: QueryClient;
  readonly consoleVersion: string;
}

// plan-019 REG (ED-26; sole owner of these lines in plan-019): the root route's component, once — WAPP-HOST mounts the help
// widget and the fill registry's provider there, never in `shell/shell.tsx`. Until then it renders the bare `<Outlet />`
// the root rendered with no component.
const rootRoute = createRootRouteWithContext<RouterContext>()({ component: HelpRoot });

// --- the two unauthenticated entry screens --------------------------------------------------

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  staticData: { pageTitle: () => portalText("shell", "page.create_your_account") },
  // plan-020 WAPP-BILL (ED-22): the website's plan buttons sign up with `?plan=<offer code>`.
  validateSearch: (raw: Record<string, unknown>): EntrySearch => entrySearchOf(raw),
  component: SignUpRoute,
});

function SignUpRoute(): ReturnType<typeof AuthScreen> {
  const search = signUpRoute.useSearch();
  return <AuthScreen intent="sign-up" {...(search.plan === undefined ? {} : { offerCode: search.plan })} />;
}

const logInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/log-in",
  staticData: { pageTitle: () => portalText("shell", "page.log_in") },
  component: () => <AuthScreen intent="log-in" />,
});

// An invitation link (design §16.37): public, like the two above — the invitee has no session yet.
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join",
  staticData: { pageTitle: () => portalText("shell", "page.join_your_team") },
  component: () => <JoinScreen />,
});

// plan-019 REG (OD-24): the help assistant's plain fallback page, outside the shell like the entry screens above (an
// anonymous visitor reaches it too). A stub until WAPP-HOST draws it; no page title until then.
const helpFallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help",
  component: HelpFallbackScreen,
});

// --- everything behind the shell -------------------------------------------------------------

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  async beforeLoad({ context }) {
    const current = await context.queryClient.ensureQueryData(authSessionQuery(context.gateway));
    if (current === null) throw redirect({ to: "/log-in" });
    // plan-034 WAPP-APPSIGNIN: a sign-in the wetalk app's consent page sent through `/log-in` lands here on every path
    // (a password, an emailed code, a provider, single sign-on, a chooser) — and goes back to that page, once.
    const appSignIn = appSignInReturnOf(tabStorage(), Date.now());
    if (appSignIn !== null) throw redirect({ to: "/app-sign-in", search: appSignIn, replace: true });
    // The workspace's name is the listing's (customer-portal review #150). Read it before the first
    // paint, as the sign-in used to, unless it is here already or already failed. Prefetching never
    // throws: a failed listing is the shell's to show, never a reason to take the shell down.
    // Only with a workspace open (plan-014 A4): the organization alone reads no workspace listing.
    const listing = workspaceQuery(context.gateway);
    const known = context.queryClient.getQueryState(listing.queryKey);
    if (current.kind === "workspace_selected" && known?.data === undefined && known?.status !== "error") {
      await context.queryClient.prefetchQuery(listing);
    }
  },
  component: ShellRoute,
  // A failed read of the sign-in says so, with Try again (customer-portal review #150).
  errorComponent: ShellFailure,
});

function ShellRoute(): ReturnType<typeof Shell> {
  const { consoleVersion } = shellRoute.useRouteContext();
  return <Shell consoleVersion={consoleVersion} />;
}

const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  beforeLoad() {
    throw redirect({ to: "/dashboard" });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/dashboard",
  staticData: { pageTitle: () => portalText("shell", "page.dashboard") },
  component: DashboardScreen,
});

const firstRunRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/first-run",
  staticData: { pageTitle: () => portalText("shell", "page.welcome") },
  component: FirstRunScreen,
});

/**
 * The Agents list's two controls, both shareable.
 *
 * `status` rather than `filter` because that is what it filters on, and because a bare
 * `filter` would have to be read to know what it means.
 */
interface VoiceAgentListSearch {
  readonly status: VoiceAgentFilter;
  readonly q: string;
}

const voiceAgentListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/agents",
  staticData: { pageTitle: () => portalText("shell", "page.agents") },
  validateSearch: (raw: Record<string, unknown>): VoiceAgentListSearch => ({
    status: voiceAgentFilterOf(raw["status"]),
    q: textOf(raw["q"]),
  }),
  component: VoiceAgentListRoute,
});

function VoiceAgentListRoute(): ReturnType<typeof VoiceAgentListScreen> {
  const search = voiceAgentListRoute.useSearch();
  const navigate = voiceAgentListRoute.useNavigate();
  return (
    <VoiceAgentListScreen
      filter={search.status}
      search={search.q}
      onFilter={(status) => {
        void navigate({ search: (current: VoiceAgentListSearch) => ({ ...current, status }) });
      }}
      onSearch={(q) => {
        void navigate({ search: (current: VoiceAgentListSearch) => ({ ...current, q }) });
      }}
    />
  );
}

const agentTemplatePickerRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/agents/new",
  // The prototype's top-bar crumb for the picker (`TITLES.templates`).
  staticData: { pageTitle: () => portalText("shell", "page.templates") },
  component: AgentTemplatePickerScreen,
});

/**
 * The wizard's step is `2..5` — the number its header and step list show; the picker is step 1
 * (customer-portal review #22, 2026-09-25: the URL said 3 while the header said 5 of 5).
 */
interface WizardSearch {
  readonly step: number;
  /** plan-014 GAP: the shared configuration the new agent starts from, when chosen on the picker. */
  readonly share?: string;
}

const wizardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/agents/new/$agentTemplateCode",
  staticData: { pageTitle: () => portalText("shell", "page.new_agent") },
  validateSearch: (raw: Record<string, unknown>): WizardSearch => {
    const share = wizardShareOf(raw["share"]);
    return { step: wizardStepOf(raw["step"]), ...(share === null ? {} : { share }) };
  },
  component: WizardRoute,
});

function WizardRoute(): ReturnType<typeof WizardScreen> {
  const { agentTemplateCode } = wizardRoute.useParams();
  const { step, share } = wizardRoute.useSearch();
  const navigate = wizardRoute.useNavigate();
  return (
    <WizardScreen
      agentTemplateCode={agentTemplateCode}
      step={step}
      resourceShareId={share ?? null}
      onStep={(next, replace) => {
        void navigate({ search: { step: next, ...(share === undefined ? {} : { share }) }, replace: replace === true });
      }}
    />
  );
}

const buildingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/building/$buildJobId",
  staticData: { pageTitle: () => portalText("shell", "page.building") },
  component: BuildingRoute,
});

function BuildingRoute(): ReturnType<typeof BuildingScreen> {
  const { buildJobId } = buildingRoute.useParams();
  return <BuildingScreen buildJobId={buildJobId} />;
}

const voiceAgentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/agents/$voiceAgentId",
  staticData: { pageTitle: () => portalText("shell", "page.agent"), pageTitleIsVoiceAgentName: true },
  validateSearch: (raw: Record<string, unknown>): VoiceAgentScreenSearch => ({
    tab: tabOf(raw["tab"]),
    range: rangeOf(raw["range"]),
    outcome: outcomeOf(raw["outcome"]),
    q: textOf(raw["q"]),
    conversation: textOf(raw["conversation"]),
    channelLabel: historyChannelLabelOf(raw["channelLabel"]),
    party: textOf(raw["party"]),
    number: textOf(raw["number"]),
    analyticsRange: analyticsRangeOf(raw["analyticsRange"]),
    agentVersion: textOf(raw["agentVersion"]),
    concern: concernOf(raw["concern"]),
    orderStatus: orderStatusOf(raw["orderStatus"]),
    // The Results tab's own filters (survey VoiceAgents, 2026-10-03), named apart from History's and
    // Analytics' for the reason `analyticsRange` is: one shared parameter would re-filter another tab.
    resultRange: surveyRangeOf(raw["resultRange"]),
    resultIncludeIsTest: raw["resultIncludeIsTest"] === true || raw["resultIncludeIsTest"] === "true",
    resultCampaign: textOf(raw["resultCampaign"]),
    resultLanguage: textOf(raw["resultLanguage"]),
    resultVariant: surveyVariantOf(raw["resultVariant"]),
    resultView: surveyViewOf(raw["resultView"]),
    resultOutcome: respondentStatusOf(raw["resultOutcome"]),
  }),
  component: VoiceAgentRoute,
});

function VoiceAgentRoute(): ReturnType<typeof VoiceAgentScreen> {
  const { voiceAgentId } = voiceAgentRoute.useParams();
  const search = voiceAgentRoute.useSearch();
  const navigate = voiceAgentRoute.useNavigate();
  return (
    <VoiceAgentScreen
      voiceAgentId={voiceAgentId}
      search={search}
      onSearch={(next) => {
        void navigate({ search: (current: VoiceAgentScreenSearch) => ({ ...current, ...next }) });
      }}
    />
  );
}

// --- Numbers & SIP and Messaging (P11-05, P11-06) ---------------------------------------------

const numbersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/numbers",
  staticData: { pageTitle: () => portalText("shell", "page.numbers_sip") },
  component: NumbersScreen,
});

const messagingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/messaging",
  staticData: { pageTitle: () => portalText("shell", "page.messaging") },
  component: MessagingScreen,
});

/**
 * The Channel detail.
 *
 * `$mediumCode` is a path parameter rather than a search parameter because it names WHICH
 * screen this is, not how it is filtered — and because the three states the screen can be in
 * are decided by the server from that code, never by the URL.
 */
/**
 * `?agent=<voice_agent_id>` (plan-004 WC-81, FR-WC-19, review #179): the Channels tab's "Get
 * snippet" names the voice agent the reader came from, and the Web chat page pre-selects it in
 * Connect. Optional — the Messaging list links without it — and anything that is not a non-empty
 * string is dropped rather than guessed at.
 */
interface MediumSearch {
  // glossary-lint-disable-next-line agent -- the public ?agent= query parameter name (FR-WC-19)
  readonly agent?: string;
}

const mediumRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/messaging/$mediumCode",
  staticData: { pageTitle: () => portalText("shell", "page.channel") },
  validateSearch: (raw: Record<string, unknown>): MediumSearch => {
    const requested = raw["agent"];
    // glossary-lint-disable-next-line agent -- the public ?agent= query parameter name (FR-WC-19)
    return typeof requested === "string" && requested !== "" ? { agent: requested } : {};
  },
  component: MediumRoute,
});

function MediumRoute(): ReturnType<typeof MediumScreen> {
  const { mediumCode } = mediumRoute.useParams();
  const search = mediumRoute.useSearch();
  return <MediumScreen mediumCode={mediumCode} requestedVoiceAgentId={search.agent ?? null} />;
}

// --- Billing, Workspaces, Settings and the Test console (P11-07, P11-08, P11-09) --------------

const billingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/billing",
  staticData: { pageTitle: () => portalText("shell", "page.billing") },
  // plan-020 WAPP-BILL: `?pay={operation}` (the pay page every plan mail links to) and `?card=replace`.
  validateSearch: (raw: Record<string, unknown>): BillingSearch => billingSearchOf(raw),
  component: BillingRoute,
});

function BillingRoute(): ReturnType<typeof BillingScreen> {
  const search = billingRoute.useSearch();
  const navigate = billingRoute.useNavigate();
  return (
    <BillingScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next, replace: true });
      }}
    />
  );
}

/**
 * The money rows.
 *
 * `/billing/ledger`, not `/billing/transactions`: the screen title is customer-facing copy and
 * the path is an identifier, and the glossary draws the line exactly there. Paging is
 * component state rather than a search parameter, because a ledger cursor is an opaque
 * position in an append-heavy list and a shared URL carrying one would resume somebody else's
 * page at a row that has since moved.
 */
const ledgerRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/billing/ledger",
  staticData: { pageTitle: () => portalText("shell", "page.transactions") },
  component: LedgerScreen,
});

/**
 * Payouts — plan-005 OP-38: what WeTalk owes the shop for its paid online orders, the statement,
 * and where payouts go. The book and the period are component state rather than search
 * parameters: the period is relative to today in Athens, and a shared URL carrying fixed days
 * would open somebody else's month.
 */
const payoutsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/payouts",
  staticData: { pageTitle: () => portalText("shell", "page.payouts") },
  component: PayoutsScreen,
});

/**
 * Live activity — 2026-09-29: every conversation in flight on every VoiceAgent, the sidebar's
 * "Live activity" row. The chosen conversation is component state: the list changes every few
 * seconds, and a shared URL naming a conversation that has ended would open onto nothing.
 */
const liveActivityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/live",
  staticData: { pageTitle: () => portalText("shell", "page.live_activity") },
  component: LiveActivityScreen,
});

/**
 * Design sync 2026-10-09: the design dropped its `Workspaces` screen — the list is Organization → Workspaces now
 * (`/organization/workspaces`, below). The old path is kept so a bookmark or an emailed link still lands on the list
 * instead of a 404; `?new` (the dialog) is carried over.
 */
const workspacesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/workspaces",
  staticData: { pageTitle: () => portalText("shell", "page.workspaces") },
  validateSearch: (raw: Record<string, unknown>): WorkspacesSearch => workspacesSearchOf(raw),
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/organization/workspaces", search, replace: true });
  },
});

const workspaceDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/workspaces/$workspaceId",
  staticData: { pageTitle: () => portalText("shell", "page.workspace") },
  component: WorkspaceDetailRoute,
});

function WorkspaceDetailRoute(): ReturnType<typeof WorkspaceDetailScreen> {
  const { workspaceId } = workspaceDetailRoute.useParams();
  return <WorkspaceDetailScreen workspaceId={workspaceId} />;
}

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings",
  staticData: { pageTitle: () => portalText("shell", "page.settings") },
  component: SettingsScreen,
});

/**
 * Order detail — `data-screen-label="Order"`, P12-03.
 *
 * A top-level route rather than a child of the agent, because the reference is the thing a
 * person has: it is printed on the ticket, spoken on the call and typed into the search box,
 * and `/orders/PZ-4A12` is a link a shop owner can be given without also being told which
 * agent took it. `agent` is a search parameter so "back to the order book" knows where to
 * return to; a bookmark without it still opens the order.
 */
interface CustomerOrderScreenSearch {
  readonly voiceAgent: string;
}

const customerOrderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/orders/$customerOrderReference",
  staticData: { pageTitle: () => portalText("shell", "page.order") },
  validateSearch: (raw: Record<string, unknown>): CustomerOrderScreenSearch => ({
    voiceAgent: textOf(raw["voiceAgent"]),
  }),
  component: CustomerOrderRoute,
});

function CustomerOrderRoute(): ReturnType<typeof CustomerOrderScreen> {
  const { customerOrderReference } = customerOrderRoute.useParams();
  const { voiceAgent } = customerOrderRoute.useSearch();
  return (
    <CustomerOrderScreen
      customerOrderReference={customerOrderReference}
      voiceAgentId={voiceAgent}
    />
  );
}

/**
 * `?agent=` — the agent the console tests (customer-portal review #169). Optional, so every link to
 * the console still reads as it did; a link from an agent's page names it, and the console keeps
 * it in the URL as the owner changes agent, so a reload or a shared link opens the same one.
 */
interface SandboxConsoleSearch {
  // glossary-lint-disable-next-line agent -- the ?agent= query parameter's own name (the UI-copy exemption)
  readonly agent?: string;
}

const sandboxConsoleRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/test-console",
  staticData: { pageTitle: () => portalText("shell", "page.test_console") },
  validateSearch: (raw: Record<string, unknown>): SandboxConsoleSearch => {
    const named = textOf(raw["agent"]);
    // glossary-lint-disable-next-line agent -- the ?agent= query parameter's own name (the UI-copy exemption)
    return named === "" ? {} : { agent: named };
  },
  component: SandboxConsoleRoute,
});

function SandboxConsoleRoute(): ReturnType<typeof SandboxConsoleScreen> {
  const search = sandboxConsoleRoute.useSearch();
  const navigate = sandboxConsoleRoute.useNavigate();
  return (
    <SandboxConsoleScreen
      voiceAgentId={search.agent ?? ""}
      onVoiceAgent={(voiceAgentId) => {
        // glossary-lint-disable-next-line agent -- the ?agent= query parameter's own name (the UI-copy exemption)
        void navigate({ search: { agent: voiceAgentId }, replace: true });
      }}
    />
  );
}

// --- Outbound, compliance and the audit log (P14) ----------------------------------------------
//
// SEVEN routes for eight screens, on purpose. **Opt out is not here.** It is the
// unauthenticated page at `<public site>/stop/<business-slug>`, it is scoped to one business
// (AC-32), and `docs/design/screen-manifest.json` assigns it to `apps/web-public`. Registering
// it inside the console would put it behind `shellRoute`'s session guard — the one thing it
// must not be behind — and would serve it from the console's origin rather than the one
// printed on a customer's receipts. What the console has instead is `optOutPageLink`, which
// composes that URL from the public site's origin exactly as the Shop view's link is composed.

const contactListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/contact-lists",
  staticData: { pageTitle: () => portalText("shell", "page.contact_lists") },
  component: ContactListScreen,
});

// One saved list: its people, its skipped rows, rename and rules (review 2026-09-25, CL #121).
const contactListDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/contact-lists/$contactListId",
  staticData: { pageTitle: () => portalText("shell", "page.contact_list") },
  component: ContactListDetailRoute,
});

function ContactListDetailRoute(): ReturnType<typeof ContactListDetailScreen> {
  const { contactListId } = contactListDetailRoute.useParams();
  return <ContactListDetailScreen key={contactListId} contactListId={contactListId} />;
}

/**
 * New campaign.
 *
 * The agent is a search parameter rather than a path segment, for the reason Order detail's
 * is: the campaign belongs to an agent, but the screen is reached from two places — the
 * agent's Outbound tab and the "Use in campaign" button on a contact list — and only one of
 * them knows which agent before the list is chosen.
 */
interface NewCampaignSearch {
  readonly voiceAgent: string;
  readonly contactList: string;
}

const newCampaignRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns/new",
  staticData: { pageTitle: () => portalText("shell", "page.new_campaign") },
  validateSearch: (raw: Record<string, unknown>): NewCampaignSearch => ({
    voiceAgent: textOf(raw["voiceAgent"]),
    contactList: textOf(raw["contactList"]),
  }),
  component: NewCampaignRoute,
});

function NewCampaignRoute(): ReturnType<typeof NewCampaignScreen> {
  const { voiceAgent, contactList } = newCampaignRoute.useSearch();
  return <NewCampaignScreen voiceAgentId={voiceAgent} contactListId={contactList} />;
}

const campaignRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns/$campaignId",
  staticData: { pageTitle: () => portalText("shell", "page.campaign") },
  component: CampaignRoute,
});

function CampaignRoute(): ReturnType<typeof CampaignScreen> {
  const { campaignId } = campaignRoute.useParams();
  return <CampaignScreen campaignId={campaignId} />;
}

/**
 * Edit a campaign before it starts (plan-015 G16/G17, design §28.4.2 `PATCH /v1/campaigns/{campaign_id}`).
 *
 * plan-015 REG (ED-15) registers the path once, so CMP never edits this file: it renders the existing
 * new-campaign screen, with New campaign's own search parameters, until CMP gives that screen its edit mode
 * (plan-015 §3 CMP: "reuses the new-campaign screen for edit on REG's route"). The screen reads the campaign
 * from the path itself — `useParams({ strict: false }).campaignId` is defined only on this route — so the
 * edit mode needs no new prop here. `key` remounts the screen when the campaign changes.
 */
const editCampaignRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns/$campaignId/edit",
  staticData: { pageTitle: () => portalText("shell", "page.edit_campaign") },
  validateSearch: (raw: Record<string, unknown>): NewCampaignSearch => ({
    voiceAgent: textOf(raw["voiceAgent"]),
    contactList: textOf(raw["contactList"]),
  }),
  component: EditCampaignRoute,
});

function EditCampaignRoute(): ReturnType<typeof NewCampaignScreen> {
  const { campaignId } = editCampaignRoute.useParams();
  const { voiceAgent, contactList } = editCampaignRoute.useSearch();
  return <NewCampaignScreen key={campaignId} voiceAgentId={voiceAgent} contactListId={contactList} />;
}

/**
 * Survey results.
 *
 * AMENDED 2026-10-03 (results read from `survey_answer`): there are no rounds, so `round` is gone; the
 * page's filters are in the URL instead — the window, test conversations, language and arm — beside
 * `view` and `status`, so a filtered page is a link somebody sends.
 */
interface SurveyResultSearch {
  readonly range: SurveyResultRangeView;
  readonly includeIsTest: boolean;
  readonly language: string;
  readonly variant: SurveyVariantView | "";
  readonly view: SurveyResultViewMode;
  readonly status: RespondentFilterValue;
}

const surveyResultRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns/$campaignId/results",
  staticData: { pageTitle: () => portalText("shell", "page.survey_results") },
  validateSearch: (raw: Record<string, unknown>): SurveyResultSearch => ({
    range: surveyRangeOf(raw["range"]),
    includeIsTest: raw["includeIsTest"] === true || raw["includeIsTest"] === "true",
    language: textOf(raw["language"]),
    variant: surveyVariantOf(raw["variant"]),
    view: surveyViewOf(raw["view"]),
    status: respondentStatusOf(raw["status"]),
  }),
  component: SurveyResultRoute,
});

function SurveyResultRoute(): ReturnType<typeof SurveyResultScreen> {
  const { campaignId } = surveyResultRoute.useParams();
  const search = surveyResultRoute.useSearch();
  const navigate = surveyResultRoute.useNavigate();
  return (
    <SurveyResultScreen
      campaignId={campaignId}
      filter={{
        range: search.range,
        includeIsTest: search.includeIsTest,
        languageCode: search.language === "" ? null : search.language,
        variant: search.variant === "" ? null : search.variant,
      }}
      view={search.view}
      status={search.status}
      onSearch={(next) => {
        void navigate({
          search: (current: SurveyResultSearch) => ({
            ...current,
            ...(next.range === undefined ? {} : { range: next.range }),
            ...(next.includeIsTest === undefined ? {} : { includeIsTest: next.includeIsTest }),
            ...(next.languageCode === undefined ? {} : { language: next.languageCode ?? "" }),
            ...(next.variant === undefined ? {} : { variant: next.variant ?? "" }),
            ...(next.view === undefined ? {} : { view: next.view }),
            ...(next.status === undefined ? {} : { status: next.status }),
          }),
        });
      }}
    />
  );
}

interface DoNotCallSearch {
  readonly filter: DoNotCallFilterView;
  readonly q: string;
}

const doNotCallRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/do-not-call",
  staticData: { pageTitle: () => portalText("shell", "page.do_not_call") },
  validateSearch: (raw: Record<string, unknown>): DoNotCallSearch => ({
    filter: doNotCallFilterOf(raw["filter"]),
    q: textOf(raw["q"]),
  }),
  component: DoNotCallRoute,
});

function DoNotCallRoute(): ReturnType<typeof DoNotCallScreen> {
  const search = doNotCallRoute.useSearch();
  const navigate = doNotCallRoute.useNavigate();
  return (
    <DoNotCallScreen
      filter={search.filter}
      search={search.q}
      onSearch={(next) => {
        void navigate({ search: (current: DoNotCallSearch) => ({ ...current, ...next }) });
      }}
    />
  );
}

const complianceRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/compliance",
  staticData: { pageTitle: () => portalText("shell", "page.compliance") },
  component: ComplianceScreen,
});

/**
 * The audit log.
 *
 * `/audit`, not `/audit-log`: the screen title is customer-facing copy and the path is an
 * identifier, and the glossary bans a bare `audit_log` as one. `eventClass` is `null` for
 * "Everything", which is the absence of a filter rather than a fifth value.
 */
interface AuditSearch {
  readonly eventClass: AuditEventClassView | null;
}

const auditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/audit",
  staticData: { pageTitle: () => portalText("shell", "page.audit_log") },
  validateSearch: (raw: Record<string, unknown>): AuditSearch => ({
    eventClass: auditEventClassOf(raw["eventClass"]),
  }),
  // "Everything" is the absence of a filter, so it is left out of the address rather than written
  // as the text `null` (customer-portal review item 196): every link to "Everything" reads `/audit`.
  search: { middlewares: [stripSearchParams({ eventClass: null })] },
  component: AuditRoute,
});

function AuditRoute(): ReturnType<typeof AuditEntryScreen> {
  const { eventClass } = auditRoute.useSearch();
  const navigate = auditRoute.useNavigate();
  return (
    <AuditEntryScreen
      eventClass={eventClass}
      onEventClass={(next) => {
        void navigate({ search: { eventClass: next } });
      }}
    />
  );
}

// --- Documentation (the prototype draws Docs inside the app shell; fidelity wave) -------------

const docsIndexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/docs",
  beforeLoad() {
    throw redirect({ to: "/docs/$docsArticleId", params: { docsArticleId: "start" } });
  },
});

/**
 * One article. An id that is not one of the six is not an error page: the address bar is
 * untrusted input, and the first article is the correct answer to "documentation".
 */
const docsArticleRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/docs/$docsArticleId",
  staticData: { pageTitle: () => portalText("shell", "page.documentation") },
  beforeLoad({ params }) {
    if (!isDocsArticleId(params.docsArticleId)) {
      throw redirect({ to: "/docs/$docsArticleId", params: { docsArticleId: "start" } });
    }
  },
  component: DocsArticleRoute,
});

function DocsArticleRoute(): ReturnType<typeof DocsScreen> {
  const { docsArticleId } = docsArticleRoute.useParams();
  return <DocsScreen docsArticleId={isDocsArticleId(docsArticleId) ? docsArticleId : "start"} />;
}

// --- plan-014: organization, finance, personal security, onboarding (REG, ED-15) ----------------
//
// Registered ONCE, by plan-014 REG, so no plan-014 unit edits this file. Paths are copy and follow the
// prototype (`/organization/…`, `/finance/…`); identifiers say `account` (ED-14). Each route hands its
// page the typed search its own module validates (`…SearchOf`) and a way to change it, so a page grows
// tabs or filters in the URL by editing only its module. Eleven pages sit behind the shell; creating an
// organization needs a signed-in person but no shell (a person may have no organization yet); accepting
// an invitation is public, like `/join`, because the invitee may not be signed in.

const accountOverviewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organization",
  staticData: { pageTitle: () => portalText("shell", "page.organization") },
  validateSearch: (raw: Record<string, unknown>): AccountOverviewSearch => accountOverviewSearchOf(raw),
  component: AccountOverviewRoute,
});

function AccountOverviewRoute(): ReturnType<typeof AccountOverviewScreen> {
  const search = accountOverviewRoute.useSearch();
  const navigate = accountOverviewRoute.useNavigate();
  return (
    <AccountOverviewScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

/** Organization → Workspaces (design sync 2026-10-09): every workspace an Owner or Admin may see, or the person's own. */
const accountWorkspacesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organization/workspaces",
  staticData: { pageTitle: () => portalText("shell", "page.workspaces") },
  validateSearch: (raw: Record<string, unknown>): WorkspacesSearch => workspacesSearchOf(raw),
  component: AccountWorkspacesRoute,
});

function AccountWorkspacesRoute(): ReturnType<typeof WorkspacesScreen> {
  const search = accountWorkspacesRoute.useSearch();
  const navigate = accountWorkspacesRoute.useNavigate();
  return (
    <WorkspacesScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next, replace: true });
      }}
    />
  );
}

const accountTeamRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organization/people",
  staticData: { pageTitle: () => portalText("shell", "page.people_and_roles") },
  validateSearch: (raw: Record<string, unknown>): AccountTeamSearch => accountTeamSearchOf(raw),
  component: AccountTeamRoute,
});

function AccountTeamRoute(): ReturnType<typeof AccountTeamScreen> {
  const search = accountTeamRoute.useSearch();
  const navigate = accountTeamRoute.useNavigate();
  return (
    <AccountTeamScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const accountSecurityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organization/security",
  staticData: { pageTitle: () => portalText("shell", "page.sign_in_and_access") },
  validateSearch: (raw: Record<string, unknown>): AccountSecuritySearch => accountSecuritySearchOf(raw),
  component: AccountSecurityRoute,
});

function AccountSecurityRoute(): ReturnType<typeof AccountSecurityScreen> {
  const search = accountSecurityRoute.useSearch();
  const navigate = accountSecurityRoute.useNavigate();
  return (
    <AccountSecurityScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const accountSharingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organization/sharing",
  staticData: { pageTitle: () => portalText("shell", "page.sharing_between_workspaces") },
  validateSearch: (raw: Record<string, unknown>): AccountSharingSearch => accountSharingSearchOf(raw),
  component: AccountSharingRoute,
});

function AccountSharingRoute(): ReturnType<typeof AccountSharingScreen> {
  const search = accountSharingRoute.useSearch();
  const navigate = accountSharingRoute.useNavigate();
  return (
    <AccountSharingScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const accountLifecycleRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organization/archive-close",
  staticData: { pageTitle: () => portalText("shell", "page.archive_restore_close") },
  validateSearch: (raw: Record<string, unknown>): AccountLifecycleSearch => accountLifecycleSearchOf(raw),
  component: AccountLifecycleRoute,
});

function AccountLifecycleRoute(): ReturnType<typeof AccountLifecycleScreen> {
  const search = accountLifecycleRoute.useSearch();
  const navigate = accountLifecycleRoute.useNavigate();
  return (
    <AccountLifecycleScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const fundingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/finance/funding",
  staticData: { pageTitle: () => portalText("shell", "page.balances_and_funding") },
  validateSearch: (raw: Record<string, unknown>): FundingSearch => fundingSearchOf(raw),
  component: FundingRoute,
});

function FundingRoute(): ReturnType<typeof FundingScreen> {
  const search = fundingRoute.useSearch();
  const navigate = fundingRoute.useNavigate();
  return (
    <FundingScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const plansRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/finance/plans",
  staticData: { pageTitle: () => portalText("shell", "page.plans") },
  validateSearch: (raw: Record<string, unknown>): PlansSearch => plansSearchOf(raw),
  component: PlansRoute,
});

function PlansRoute(): ReturnType<typeof PlansScreen> {
  const search = plansRoute.useSearch();
  const navigate = plansRoute.useNavigate();
  return (
    <PlansScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const allowanceRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/finance/allowances",
  staticData: { pageTitle: () => portalText("shell", "page.allowances_and_spend_caps") },
  validateSearch: (raw: Record<string, unknown>): AllowanceSearch => allowanceSearchOf(raw),
  component: AllowanceRoute,
});

function AllowanceRoute(): ReturnType<typeof AllowanceScreen> {
  const search = allowanceRoute.useSearch();
  const navigate = allowanceRoute.useNavigate();
  return (
    <AllowanceScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const financialDocumentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/finance/documents",
  staticData: { pageTitle: () => portalText("shell", "page.invoices_receipts_and_statements") },
  validateSearch: (raw: Record<string, unknown>): FinancialDocumentSearch => financialDocumentSearchOf(raw),
  component: FinancialDocumentRoute,
});

function FinancialDocumentRoute(): ReturnType<typeof FinancialDocumentScreen> {
  const search = financialDocumentRoute.useSearch();
  const navigate = financialDocumentRoute.useNavigate();
  return (
    <FinancialDocumentScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const postpaidRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/finance/postpaid",
  staticData: { pageTitle: () => portalText("shell", "page.postpaid") },
  validateSearch: (raw: Record<string, unknown>): PostpaidSearch => postpaidSearchOf(raw),
  component: PostpaidRoute,
});

function PostpaidRoute(): ReturnType<typeof PostpaidScreen> {
  const search = postpaidRoute.useSearch();
  const navigate = postpaidRoute.useNavigate();
  return (
    <PostpaidScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const personalSecurityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/me/security",
  staticData: { pageTitle: () => portalText("shell", "page.your_sign_in_and_devices") },
  validateSearch: (raw: Record<string, unknown>): PersonalSecuritySearch => personalSecuritySearchOf(raw),
  component: PersonalSecurityRoute,
});

function PersonalSecurityRoute(): ReturnType<typeof PersonalSecurityScreen> {
  const search = personalSecurityRoute.useSearch();
  const navigate = personalSecurityRoute.useNavigate();
  return (
    <PersonalSecurityScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

const accountCreationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/start/organization",
  staticData: { pageTitle: () => portalText("shell", "page.create_an_organization") },
  validateSearch: (raw: Record<string, unknown>): AccountCreationSearch => accountCreationSearchOf(raw),
  async beforeLoad({ context }) {
    // Signed in in ANY of the three contexts (plan-014 A4): a person with no organization yet, or one
    // choosing between several, is exactly who creates one here — only nobody signed in goes to Log in.
    const current = await context.queryClient.ensureQueryData(authContextQuery(context.gateway));
    if (current === null) throw redirect({ to: "/log-in" });
  },
  component: AccountCreationRoute,
});

function AccountCreationRoute(): ReturnType<typeof AccountCreationFlow> {
  const search = accountCreationRoute.useSearch();
  const navigate = accountCreationRoute.useNavigate();
  return (
    <AccountCreationFlow
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

// AMENDED 2026-10-06 (plan-014 B3, hand-off from INT): the token rides in the FRAGMENT (`/invitation#<token>`), like
// every MAIL link — a fragment never reaches a server log, a proxy or a Referer header.
const invitationAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitation",
  staticData: { pageTitle: () => portalText("shell", "page.accept_an_invitation") },
  validateSearch: (raw: Record<string, unknown>): InvitationAcceptSearch => invitationAcceptSearchOf(raw),
  component: InvitationAcceptRoute,
});

function InvitationAcceptRoute(): ReturnType<typeof InvitationAcceptScreen> {
  const hash = useLocation({ select: (location) => location.hash.replace(/^#/, "") });
  // plan-014 review I-15: read once and kept in state, then taken out of the address bar and the history entry (the
  // mail-landing routes' `useFragmentToken`), so a later visitor of a shared machine cannot reopen the link — under
  // answer 45 the token proves the invited address.
  const [token] = useState(hash);
  const search = invitationAcceptRoute.useSearch();
  const navigate = invitationAcceptRoute.useNavigate();
  useEffect(() => {
    if (hash !== "") void navigate({ search: (current: InvitationAcceptSearch) => current, hash: "", replace: true });
  }, [hash, navigate]);
  return (
    <InvitationAcceptScreen
      token={token}
      search={search}
      onSearch={(next) => {
        void navigate({ search: next });
      }}
    />
  );
}

// --- the validators ---------------------------------------------------------------------------

function tabOf(raw: unknown): VoiceAgentTabId {
  return (typeof raw === "string" ? voiceAgentTabOfAddress(raw) : null) ?? "live";
}

const RANGE = ["today", "yesterday", "7d", "30d", "90d", "all"] as const;
function rangeOf(raw: unknown): HistoryRange {
  return typeof raw === "string" && (RANGE as readonly string[]).includes(raw)
    ? (raw as HistoryRange)
    : // The prototype's History opens on "Last 7 days".
      "7d";
}

/**
 * History's outcome chip (review #113, #122): a fixed filter or one outcome in the template's own
 * words. The two chips renamed on 2026-09-25 still arrive in old links and are read as what they
 * meant: `completed` was any outcome, `abandoned` was none.
 */
const OUTCOME_LEGACY: Readonly<Record<string, HistoryOutcomeFilter>> = {
  completed: "reached",
  abandoned: "none",
};
const OUTCOME_MAX = 80;
export function outcomeOf(raw: unknown): HistoryOutcomeFilter {
  const text = searchTextOf(raw).trim();
  if (text === "" || text.length > OUTCOME_MAX) return "all";
  return OUTCOME_LEGACY[text] ?? text;
}

/** History's channel chip (plan-004 WC-90): one of the API's labels, or every label. */
const HISTORY_CHANNEL_LABEL = [
  "phone",
  "browser_test",
  "website_voice",
  "web_chat",
  "test_chat",
  "whatsapp",
  "viber",
] as const;
export function historyChannelLabelOf(raw: unknown): HistoryChannelLabelFilter {
  return typeof raw === "string" && (HISTORY_CHANNEL_LABEL as readonly string[]).includes(raw)
    ? (raw as HistoryChannelLabelFilter)
    : "all";
}

function voiceAgentFilterOf(raw: unknown): VoiceAgentFilter {
  return typeof raw === "string" && isVoiceAgentFilter(raw) ? raw : "all";
}

const ANALYTICS_RANGE = ["24h", "7d", "14d", "30d", "90d", "all"] as const;
function analyticsRangeOf(raw: unknown): AnalyticsRange {
  return typeof raw === "string" && (ANALYTICS_RANGE as readonly string[]).includes(raw)
    ? (raw as AnalyticsRange)
    : "14d";
}

function orderStatusOf(raw: unknown): CustomerOrderFilter {
  return typeof raw === "string" && isCustomerOrderFilter(raw) ? raw : "all";
}

function concernOf(raw: unknown): ConcernPane {
  return typeof raw === "string" && isConcernPane(raw) ? raw : "gaps";
}

/**
 * A free-text search parameter, kept as the text it was.
 *
 * TanStack's default `parseSearch` JSON-parses every value, so `?q=4567` arrives as the NUMBER 4567
 * (and `?q=true` as a boolean). Dropping anything that is not a string turned the do-not-call
 * search for the last four digits of a number — the search the screen invites — into no search at
 * all, on every route that reads `q` (register 0z.2). A number or a boolean is written back as the
 * text it was parsed from; anything else (an object, an array, absent) is no text.
 */
export function searchTextOf(raw: unknown): string {
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  return "";
}

const textOf = searchTextOf;

const SURVEY_VIEW = ["question", "person"] as const;
function surveyViewOf(raw: unknown): SurveyResultViewMode {
  return typeof raw === "string" && (SURVEY_VIEW as readonly string[]).includes(raw)
    ? (raw as SurveyResultViewMode)
    : "question";
}

const RESPONDENT_STATUS = ["all", "completed", "partial", "declined", "no_answer", "no_outcome"] as const;
function respondentStatusOf(raw: unknown): RespondentFilterValue {
  return typeof raw === "string" && (RESPONDENT_STATUS as readonly string[]).includes(raw)
    ? (raw as RespondentFilterValue)
    : "all";
}

/** A survey results window; absent or unknown is every conversation (`all`). */
function surveyRangeOf(raw: unknown): SurveyResultRangeView {
  return typeof raw === "string" && isSurveyResultRange(raw) ? raw : "all";
}

function surveyVariantOf(raw: unknown): SurveyVariantView | "" {
  return raw === "A" || raw === "B" ? raw : "";
}

const DO_NOT_CALL_FILTER = ["all", "said_on_call", "opt_out_page", "added_by_member"] as const;
function doNotCallFilterOf(raw: unknown): DoNotCallFilterView {
  return typeof raw === "string" && (DO_NOT_CALL_FILTER as readonly string[]).includes(raw)
    ? (raw as DoNotCallFilterView)
    : "all";
}

/** `null` is "Everything", which is the absence of a filter rather than a fifth value. */
const AUDIT_EVENT_CLASS = ["voice_agent", "outbound", "billing", "compliance", "person"] as const;
function auditEventClassOf(raw: unknown): AuditEventClassView | null {
  return typeof raw === "string" && (AUDIT_EVENT_CLASS as readonly string[]).includes(raw)
    ? (raw as AuditEventClassView)
    : null;
}

// --- assembly ------------------------------------------------------------------------------------

const routeTree = rootRoute.addChildren([
  signUpRoute,
  joinRoute,
  logInRoute,
  impersonationHandoffRouteUnder(rootRoute),
  confirmOperatorActionRouteUnder(rootRoute),
  // plan-034 WAPP-APPSIGNIN
  appSignInRouteUnder(rootRoute),
  appSignInFinishRouteUnder(rootRoute),
  verifyEmailRouteUnder(rootRoute),
  emailChangeRouteUnder(rootRoute),
  forgotPasswordRouteUnder(rootRoute),
  resetPasswordRouteUnder(rootRoute),
  // plan-014 REG
  accountCreationRoute,
  invitationAcceptRoute,
  // plan-019 REG
  helpFallbackRoute,
  shellRoute.addChildren([
    indexRoute,
    dashboardRoute,
    liveActivityRoute,
    firstRunRoute,
    voiceAgentListRoute,
    agentTemplatePickerRoute,
    wizardRoute,
    buildingRoute,
    voiceAgentRoute,
    numbersRoute,
    messagingRoute,
    mediumRoute,
    billingRoute,
    ledgerRoute,
    payoutsRoute,
    workspacesRoute,
    workspaceDetailRoute,
    settingsRoute,
    customerOrderRoute,
    sandboxConsoleRoute,
    contactListRoute,
    contactListDetailRoute,
    newCampaignRoute,
    campaignRoute,
    // plan-015 REG: the campaign edit path (CMP fills the screen's edit mode).
    editCampaignRoute,
    surveyResultRoute,
    doNotCallRoute,
    complianceRoute,
    auditRoute,
    docsIndexRoute,
    docsArticleRoute,
    // plan-014 REG
    accountOverviewRoute,
    accountWorkspacesRoute,
    accountTeamRoute,
    accountSecurityRoute,
    accountSharingRoute,
    accountLifecycleRoute,
    fundingRoute,
    plansRoute,
    allowanceRoute,
    financialDocumentRoute,
    postpaidRoute,
    personalSecurityRoute,
  ]) as AnyRoute,
]);

/**
 * `history` is injected so a suite can drive the console with an in-memory history and no
 * browser URL bar. It is the only difference between the router a person uses and the one a
 * journey test drives — the tree, the validators and the guards are the same object.
 */
export function buildRouter(
  context: RouterContext,
  history?: RouterHistory,
): ReturnType<typeof createRouter> {
  return createRouter({
    routeTree,
    context,
    defaultPreload: "intent",
    // 2026-10-14: a page that fails while drawing says so, with Try again, inside the shell — never a blank area.
    defaultErrorComponent: PageFailure,
    scrollRestoration: true,
    // Review #121: text parameters are written as typed (`q=6947130688`, never `q="6947130688"`).
    parseSearch: parseConsoleSearch,
    stringifySearch: stringifyConsoleSearch,
    ...(history === undefined ? {} : { history }),
  });
}

export type ConsoleRouter = ReturnType<typeof buildRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: ConsoleRouter;
  }
}
